import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  BarChart3,
  Shield,
  LogOut,
  Users,
  UserCog,
  UserCircle2,
  Database,
  Smartphone,
  Key,
  FileJson,
  Archive,
  Bot,
  ToggleLeft,
  ToggleRight,
  Timer,
} from 'lucide-react';
import { useAuth } from '@/state/auth-context';
import { Button } from '@/components/ui/button';

/**
 * Root Layout Component
 *
 * Provides navigation and layout structure for all protected routes.
 * Includes responsive navigation with mobile-friendly design.
 */
export function RootLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, state } = useAuth();

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
    },
    {
      name: 'Users Migration',
      href: '/users-migration',
      icon: Users,
    },
    {
      name: 'User Management',
      href: '/user-management',
      icon: UserCog,
    },
    {
      name: 'Team',
      href: '/team',
      icon: UserCircle2,
    },
    {
      name: 'App Versions',
      href: '/app-versions',
      icon: Smartphone,
    },
    {
      name: 'API Keys',
      href: '/api-keys',
      icon: Key,
    },
    {
      name: 'Cache',
      href: '/cache-management',
      icon: Database,
    },
    {
      name: 'Group Orders',
      href: '/group-orders',
      icon: Timer,
    },
    {
      name: 'Archive',
      href: '/archive-management',
      icon: Archive,
    },
    {
      name: 'Global Config',
      href: '/global-config',
      icon: FileJson,
    },
    {
      name: 'Feature Flags',
      href: '/feature-flags',
      icon: ToggleLeft,
    },
    {
      name: 'Tenant Flags',
      href: '/tenant-feature-flags',
      icon: ToggleRight,
    },
    {
      name: 'Analytics',
      href: '/analytics',
      icon: BarChart3,
    },
    {
      name: 'Security',
      href: '/security',
      icon: Shield,
    },
    {
      name: 'Log Debugger',
      href: '/log-debugger',
      icon: Bot,
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  /**
   * Handle Logout
   * Clears auth state and redirects to login
   */
  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex h-screen flex-col">
      {/* Header */}
      <header className="border-b bg-white">
        <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <Shield className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-lg font-semibold">Admin Dashboard</h1>
              <p className="text-xs text-muted-foreground">{state.user?.email || 'Admin Portal'}</p>
            </div>
          </div>

          <Button variant="ghost" size="sm" onClick={handleLogout} className="gap-2">
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar Navigation */}
        <nav className="hidden w-64 border-r bg-white md:block">
          <div className="flex h-full flex-col p-4">
            <div className="space-y-2">
              {navigation.map(item => {
                const Icon = item.icon;
                return (
                  <Link key={item.name} to={item.href}>
                    <Button
                      variant={isActive(item.href) ? 'secondary' : 'ghost'}
                      className="w-full justify-start gap-3"
                    >
                      <Icon className="h-5 w-5" />
                      {item.name}
                    </Button>
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>

        {/* Mobile Navigation */}
        <nav className="fixed bottom-0 left-0 right-0 z-50 border-t bg-white md:hidden">
          <div className="flex items-center justify-around p-2">
            {navigation.map(item => {
              const Icon = item.icon;
              return (
                <Link key={item.name} to={item.href} className="flex-1">
                  <Button
                    variant={isActive(item.href) ? 'secondary' : 'ghost'}
                    size="sm"
                    className="w-full flex-col gap-1 h-auto py-2"
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-xs">{item.name}</span>
                  </Button>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Main Content Area */}
        <main className="flex-1 overflow-auto bg-gray-50 pb-16 md:pb-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
